import _ from 'underscore';
import React from 'react';
import PropTypes from 'prop-types';
import $ from 'jquery';
import Promise from 'bluebird';

import { withUiBlock } from '@bingads-webui/uiblocker';
import { createAsyncToken } from '@bingads-webui-universal/primitive-utilities';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';

import { normalize } from '../normalize-data';
import { getPreferences, isCompetition } from '../util';
import {
  VIEW_TYPES,
  CHANNEL_TYPES,
  COMPETITION_SUMMARY_TOP_NUMBER,
  RECOMMENDATION_SUMMARY_TOP_NUMBER,
  CampaignType,
  RECOMMENDATION_TYPES,
} from '../consts';

export const withGetSummary = (
  withDetails,
  needExamples = true
) => (WrappedComponent) => {
  class HoC extends React.Component {
    static displayName = `withGetSummary(${getDisplayName(WrappedComponent)})`;

    static propTypes = {
      type: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
      viewType: PropTypes.string,
      recommendationType: PropTypes.string,
      channel: PropTypes.string.isRequired,
      campaignType: PropTypes.number,
      categoryType: PropTypes.string,
      dataService: PropTypes.shape({
        getSummary: PropTypes.func.isRequired,
      }).isRequired,
      /**
       * element to be covered by uiblocker when loading data.
       */
      el: PropTypes.string.isRequired,
      filterService: PropTypes.objectOf(PropTypes.any),
      /**
       * page size information is stored in preference under preferencesName.
       */
      preferencesName: PropTypes.string,
      deps: PropTypes.shape({
        preferencesService: PropTypes.objectOf(PropTypes.any),
        i18n: PropTypes.shape({
          getString: PropTypes.func,
        }),
      }),
      vnextDeps: PropTypes.shape({
        eventDispatcher: PropTypes.objectOf(PropTypes.any),
      }),
      defaultPageSize: PropTypes.number,
      defaultPageNumber: PropTypes.number,
      /**
       * this is default options for reload. It will use this to do first reload in mount.
       * For explicit call of reload, this can be overidden by argument.
       */
      defaultOptions: PropTypes.objectOf(PropTypes.any),
      perfMarker: PropTypes.shape({
        willInit: PropTypes.func.isRequired,
        willFetchData: PropTypes.func.isRequired,
        willRender: PropTypes.func.isRequired,
        done: PropTypes.func.isRequired,
        createChild: PropTypes.func.isRequired,
      }),
      withBlocker: PropTypes.bool,
    };

    static defaultProps = {
      campaignType: null,
      type: null,
      viewType: null,
      recommendationType: null,
      preferencesName: null,
      deps: null,
      defaultPageSize: null,
      defaultPageNumber: null,
      defaultOptions: {},
      filterService: null,
      perfMarker: {
        willInit: _.noop,
        willFetchData: _.noop,
        willRender: _.noop,
        done: _.noop,
        createChild() {
          return this;
        },
      },
      withBlocker: true,
      categoryType: '',
      vnextDeps: null,
    };

    constructor(props) {
      super(props);
      this.perfMarkerDone = false;
      this.eventDispatcherDone = false;
    }

    state = {
      data: {},
      campaignAdGroups: null,
    };

    componentDidMount = () => {
      // In account level recommendation detail page, it doesn't need to call summary Odata.
      // Because category information is included in detail Odata resposne already.
      if (this.props.channel === CHANNEL_TYPES.RECOMMENDATION
        && this.props.viewType === VIEW_TYPES.DETAILS) {
        this.props.perfMarker.willFetchData();
      } else {
        this.reload(this.props.defaultOptions);
      }
    };

    componentDidUpdate = (prevProps, prevState) => {
      // We only care the first render after data first turn non null
      if (_.isEqual(prevState.data, {})) {
        if (this.props.perfMarker
          && (this.props.channel === CHANNEL_TYPES.RECOMMENDATION
          || this.props.channel === CHANNEL_TYPES.MCCRECOMMENDATION)
          && !this.perfMarkerDone) {
          this.perfMarkerDone = true;
          this.props.perfMarker.done();
        }
        if (this.props.viewType !== VIEW_TYPES.DETAILS &&
          !_.isNull(this.props.vnextDeps) &&
          !_.isUndefined(this.props.vnextDeps.eventDispatcher)) {
          this.props.vnextDeps.eventDispatcher.trigger('recommendation_view_loaded');
        }
      }
    };

    componentWillUnmount = () => {
      this.cancel();
    };

    setData = (data, campaignAdGroups, setDataFromDetails) => {
      setDataFromDetails();
      if (_.isEqual(this.state.data, {})) {
        this.setState({
          data: normalize(data, withDetails),
          // Gained, Dismissed, Available
          overallOptimizationScoreBar: data.OverallOptimizationScoreBar,
          // [ScoreUpLift, Category]
          categoryScoreUpLifts: data.CategoryScoreUpLifts,
          availableCategories: data.AvailableCategories,
          campaignAdGroups,
        });
      }
    };

    hasLoadToken = () => !_.isUndefined(this.loadToken);

    reload = (op = {}) => {
      this.cancel();

      _.defaults(op, this.props.defaultOptions);

      const {
        type,
        viewType,
        recommendationType,
        channel,
        campaignType,
        dataService,
        filterService,
        preferencesName,
        deps,
        defaultPageNumber,
        defaultPageSize,
        categoryType,
      } = this.props;

      let top = 5;
      if (needExamples) {
        top = isCompetition(type) ?
          COMPETITION_SUMMARY_TOP_NUMBER :
          RECOMMENDATION_SUMMARY_TOP_NUMBER;
      }
      const topOption = withDetails ? {} : { top };
      const preferencesService = deps && deps.preferencesService;

      const input = _.compact([
        filterService && filterService.p$Initialized,
        preferencesService && preferencesService.initialize(),
      ]);
      Promise.all(input)
        .spread(() => {
          let where = null;
          let pagination = {};

          if (filterService) {
            const filterQuery = filterService.getCombinedFilter();
            where = filterQuery.query;
          }

          if (preferencesName && preferencesService) {
            const preferences = getPreferences(preferencesService, preferencesName);
            this.pageSize = _.isNumber(preferences.pageSize) ?
              preferences.pageSize : defaultPageSize;
            this.pageNumber = _.isNumber(op.pageNumber) ? op.pageNumber : defaultPageNumber;
            pagination = {
              top: this.pageSize,
              skip: this.pageNumber * this.pageSize,
            };
          }

          const shouldShowAlertHandler = viewType !== VIEW_TYPES.SUMMARY ||
            channel === CHANNEL_TYPES.COMPETITION;

          const options = _.defaults({}, topOption, {
            type,
            recommendationType,
            channel,
            campaignType,
            where,
            scope: op.scope,
            isFromApplyOrDismiss: op.isFromApplyOrDismiss,
            shouldShowAlertHandler,
            categoryType,
          }, pagination);

          if (this.props.perfMarker) {
            this.props.perfMarker.willFetchData();
          }

          this.loadToken = createAsyncToken(dataService
            .getSummary(options).finally(this.props.withBlocker ?
              withUiBlock(
                $(this.props.el),
                this.props.deps.i18n.getString('loadingText'),
                true, // shouldFocus
                null, // delayedLoading
                '1000' // overlayZIndex
              ) : _.noop))
            .on({
              onSuccess: ({
                data,
                campaignAdGroups,
              }) => {
                if (this.props.perfMarker) {
                  this.props.perfMarker.willRender();
                }
                this.setState({
                  data: normalize(data, withDetails),
                  // Gained, Dismissed, Available
                  overallOptimizationScoreBar: data.OverallOptimizationScoreBar,
                  // [ScoreUpLift, Category]
                  categoryScoreUpLifts: data.CategoryScoreUpLifts,
                  availableCategories: data.AvailableCategories,
                  campaignAdGroups,
                  hasFilter: !_.isEmpty(where),
                });
              },
            });
        });
    };

    cancel = () => {
      if (this.loadToken) {
        this.loadToken.cancel();
      }
    };

    render = () => {
      // eslint-disable-next-line max-len
      if (this.props.viewType !== VIEW_TYPES.DETAILS && _.isEqual(this.state.data, {}) && !this.hasLoadToken()) {
        return null;
      }
      const {
        data: normalizedData,
        overallOptimizationScoreBar,
        categoryScoreUpLifts,
        campaignAdGroups,
        hasFilter,
        availableCategories,
      } = this.state;

      if (this.props.viewType === VIEW_TYPES.DETAILS && this.eventDispatcherDone) {
        if (this.props.perfMarker) {
          this.props.perfMarker.willRender();
        }
      }

      if (!this.eventDispatcherDone &&
        this.props.viewType === VIEW_TYPES.DETAILS &&
        !_.isNull(this.props.vnextDeps) &&
        !_.isUndefined(this.props.vnextDeps.eventDispatcher)) {
        this.eventDispatcherDone = true;
        this.props.vnextDeps.eventDispatcher.trigger('recommendation_view_loaded');
      }
      let data = _.omit(normalizedData, 'Dismissed');
      let dismissedData = normalizedData.Dismissed || {};
      // Remove DSA recommendations for PMAX campaigns
      if (this.props.campaignType === CampaignType.PerformanceMax) {
        data = _.omit(data, RECOMMENDATION_TYPES.DYNAMIC_SEARCH_ADS);
        dismissedData = _.omit(dismissedData, RECOMMENDATION_TYPES.DYNAMIC_SEARCH_ADS);
      }
      return (<WrappedComponent
        reload={this.reload}
        data={data}
        dismissedData={dismissedData}
        campaignAdGroups={campaignAdGroups}
        setData={this.setData}
        hasLoadToken={this.hasLoadToken}
        hasFilter={hasFilter}
        overallOptimizationScoreBar={overallOptimizationScoreBar}
        categoryScoreUpLifts={categoryScoreUpLifts}
        {...this.props}
        pageSize={this.pageSize}
        pageNumber={this.pageNumber}
        availableCategories={availableCategories}
      />);
    };
  }

  return hoistNonReactStatics(HoC, WrappedComponent);
};
